import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { UsersThreeIcon, PlusIcon, TrashIcon, SkullIcon, StarIcon, XIcon, PencilSimpleIcon, CheckIcon, ShareNetworkIcon, BookOpenIcon } from "@phosphor-icons/react";
import { CreatorPlaybook } from "../components/CreatorPlaybook";
import { DevLeaderboard } from "../components/DevLeaderboard";
import { useTrustedCreators } from "../lib/useTrustedCreators";
import { useRugLedger } from "../lib/useRugLedger";
import { isValidSolanaAddress, shortAddr } from "../lib/solana";
import { swapDev } from "../lib/api";
import { getClientId } from "../lib/clientId";
import { DevShareCard } from "../components/DevShareCard";
import { HitRateBadge, useDeployerStatsBatch } from "../components/HitRateBadge";

const useDevSwap = () => {
  const qc = useQueryClient();
  const clientId = getClientId();
  return async (name, to) => {
    try {
      const res = await swapDev(clientId, { name, to });
      qc.invalidateQueries({ queryKey: ["rugs", clientId] });
      qc.invalidateQueries({ queryKey: ["trustedCreators", clientId] });
      toast.success(`${name} → moved ${res.wallets} wallet${res.wallets === 1 ? "" : "s"} to ${to === "alpha" ? "Alpha Devs" : "Blocked Devs"}`);
    } catch {
      toast.error("Couldn't move dev — try again");
    }
  };
};

const AlphaDevs = () => {
  const { devList, names, addWallet, removeWallet } = useTrustedCreators();
  const stats = useDeployerStatsBatch(devList.flatMap((d) => d.addresses));
  const swap = useDevSwap();
  const [name, setName] = useState("");
  const [addr, setAddr] = useState("");
  const [shareDev, setShareDev] = useState(null);

  const onAdd = () => {
    const n = name.trim();
    const a = addr.trim();
    if (!n) { toast.error("Give the dev a name."); return; }
    if (!isValidSolanaAddress(a)) { toast.error("Enter a valid creator wallet address."); return; }
    addWallet(n, a);
    setAddr("");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <StarIcon size={20} weight="fill" className="text-[#F0C000]" />
        <h2 className="font-heading font-bold text-xl">Alpha Devs</h2>
        <span className="font-mono text-xs text-muted-foreground">{devList.length} devs</span>
      </div>
      <p className="text-sm text-muted-foreground">Track devs by <span className="text-white font-semibold">name</span> and add every wallet they use — a launch from <span className="italic">any</span> of their wallets gets a <span className="text-[#F0C000] font-semibold">Potential Alpha</span> tag + chime across the site. Tip: on any coin that ran, open it and hit the ⭐ button to grab its creator wallet automatically.</p>

      <div className="border border-white/10 bg-card p-4 flex flex-col sm:flex-row gap-3">
        <input list="dev-name-options" data-testid="alpha-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Dev name (e.g. MrRunner)" className="sm:w-48 bg-black/40 border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-[#C9A227]/60" />
        <datalist id="dev-name-options">{names.map((n) => <option key={n} value={n} />)}</datalist>
        <input data-testid="alpha-addr-input" value={addr} onChange={(e) => setAddr(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onAdd()} placeholder="Creator wallet address…" className="flex-1 bg-black/40 border border-white/10 px-4 py-2.5 text-sm font-mono focus:outline-none focus:border-[#C9A227]/60" />
        <button data-testid="alpha-add-btn" onClick={onAdd} className="flex items-center justify-center gap-2 px-5 py-2.5 bg-[#F0C000] text-black font-semibold text-sm hover:bg-[#F0C000]/90 transition-colors"><PlusIcon size={16} weight="bold" /> Add wallet</button>
      </div>

      {devList.length === 0 ? (
        <div className="border border-white/10 bg-card py-12 text-center text-muted-foreground font-mono text-sm">No alpha devs yet. Name a dev and add a wallet above.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {devList.map((dev) => (
            <div key={dev.name} data-testid={`alpha-dev-${dev.name}`} className="border border-[#C9A227]/30 bg-[#C9A227]/[0.04] p-4 space-y-2">
              <div className="flex items-center gap-2">
                <StarIcon size={16} weight="fill" className="text-[#F0C000]" />
                <span className="font-heading font-bold text-sm">{dev.name}</span>
                <span className="font-mono text-[10px] text-muted-foreground">{dev.addresses.length} wallet{dev.addresses.length > 1 ? "s" : ""}</span>
                <div className="ml-auto flex items-center gap-1.5">
                  <button
                    data-testid={`alpha-share-${dev.name}`}
                    onClick={() => setShareDev({ status: "alpha", name: dev.name, addresses: dev.addresses })}
                    title="Share this Alpha Dev card"
                    className="flex items-center gap-1 text-[11px] px-2 py-1 border border-[#C9A227]/40 text-[#F0C000] hover:bg-[#C9A227]/10 transition-colors"
                  >
                    <ShareNetworkIcon size={12} weight="bold" /> Share
                  </button>
                  {dev.name !== "Unnamed dev" && (
                    <button
                      data-testid={`alpha-to-blocked-${dev.name}`}
                      onClick={() => swap(dev.name, "blocked")}
                      title="Betrayed you? Move this dev's whole roster to Blocked"
                      className="flex items-center gap-1 text-[11px] px-2 py-1 border border-danger/30 text-danger hover:bg-danger/10 transition-colors"
                    >
                      <SkullIcon size={12} weight="fill" /> Move to Blocked
                    </button>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {dev.addresses.map((a) => (
                  <span key={a} className="flex items-center gap-1.5 font-mono text-[11px] px-2 py-1 border border-white/10 text-muted-foreground">
                    {shortAddr(a)}
                    <HitRateBadge stats={stats[a]} compact />
                    <button data-testid={`alpha-remove-${a}`} onClick={() => removeWallet(a)} className="hover:text-danger"><XIcon size={12} weight="bold" /></button>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {shareDev && <DevShareCard {...shareDev} open onClose={() => setShareDev(null)} />}
    </div>
  );
};

const BlockedDevCard = ({ group, onRename, onSwap }) => {
  const { name, creators, flags, isNamed } = group;
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(isNamed ? name : "");
  const { unflag } = useRugLedger();
  const creatorList = [...creators];
  const rugCount = flags.length;
  const [share, setShare] = useState(false);

  return (
    <div data-testid={`blocked-dev-${name}`} className="border border-danger/30 bg-danger/[0.04] p-4 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {editing ? (
          <div className="flex items-center gap-1 flex-1">
            <input autoFocus value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (onRename(creatorList, val), setEditing(false))} placeholder="Dev name" className="flex-1 bg-black/40 border border-white/10 px-2 py-1 text-sm focus:outline-none focus:border-danger/50" />
            <button data-testid={`blocked-rename-save-${name}`} onClick={() => { onRename(creatorList, val); setEditing(false); }} className="p-1.5 bg-danger text-white"><CheckIcon size={14} weight="bold" /></button>
          </div>
        ) : (
          <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 group font-heading font-bold text-sm">
            <SkullIcon size={16} weight="fill" className="text-danger" />
            {isNamed ? name : <span className="text-muted-foreground italic">Name this dev</span>}
            <PencilSimpleIcon size={13} className="text-muted-foreground group-hover:text-danger" />
          </button>
        )}
        <div className="flex items-center gap-2 shrink-0">
          {rugCount > 0 && <span className="flex items-center gap-1 font-mono text-xs px-2 py-0.5 bg-danger/15 text-danger border border-danger/30">{rugCount} rug{rugCount > 1 ? "s" : ""}</span>}
          <button
            data-testid={`blocked-share-${name}`}
            onClick={() => setShare(true)}
            title="Share this Blocked Dev card"
            className="flex items-center gap-1 text-[11px] px-2 py-1 border border-danger/40 text-danger hover:bg-danger/10 transition-colors"
          >
            <ShareNetworkIcon size={12} weight="bold" /> Share
          </button>
          {isNamed && (
            <button
              data-testid={`blocked-to-alpha-${name}`}
              onClick={() => onSwap(name)}
              title="Redeemed? Move this dev's whole roster to Alpha (clears rug flags)"
              className="flex items-center gap-1 text-[11px] px-2 py-1 border border-[#C9A227]/40 text-[#F0C000] hover:bg-[#C9A227]/10 transition-colors"
            >
              <StarIcon size={12} weight="fill" /> Move to Alpha
            </button>
          )}
        </div>
      </div>
      {creatorList.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {creatorList.map((c) => (
            <span key={c} className="font-mono text-[11px] px-2 py-1 border border-white/10 text-muted-foreground">{shortAddr(c)}</span>
          ))}
        </div>
      )}
      {flags.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-0.5">
          {flags.map((f) => (
            <Link key={f.mint} to={`/coin/${f.mint}`} className="flex items-center gap-1.5 font-mono text-xs px-2 py-1 border border-white/10 text-muted-foreground hover:text-white hover:border-white/30 transition-colors">
              {f.symbol || shortAddr(f.mint)}
              <button data-testid={`unblock-${f.mint}`} onClick={(e) => { e.preventDefault(); unflag(f.mint); }} className="hover:text-danger"><XIcon size={12} weight="bold" /></button>
            </Link>
          ))}
        </div>
      )}
      {share && <DevShareCard status="blocked" name={isNamed ? name : shortAddr(creatorList[0] || "dev")} addresses={creatorList} rugCount={rugCount} open onClose={() => setShare(false)} />}
    </div>
  );
};

const BlockedDevs = () => {
  const { flags, names, blockedSet, setName } = useRugLedger();
  const swap = useDevSwap();
  // group by dev name (from names map); wallets without a name group by short address
  const groups = {};
  const ensure = (creator) => {
    const nm = creator && names[creator];
    const key = nm || (creator ? shortAddr(creator) : "Unnamed");
    if (!groups[key]) groups[key] = { name: nm || key, creators: new Set(), flags: [], isNamed: !!nm };
    return groups[key];
  };
  flags.forEach((f) => { const g = ensure(f.creator); if (f.creator) g.creators.add(f.creator); g.flags.push(f); });
  blockedSet.forEach((c) => { const g = ensure(c); g.creators.add(c); });
  const entries = Object.values(groups).sort((a, b) => b.flags.length - a.flags.length);

  const rename = async (creators, name) => {
    if (!name.trim()) return;
    for (const c of creators) await setName(c, name.trim());
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <SkullIcon size={20} weight="fill" className="text-danger" />
        <h2 className="font-heading font-bold text-xl">Blocked Devs</h2>
      </div>
      <p className="text-sm text-muted-foreground">Every dev you've flagged for a rug. Name them to group their rotating wallets together — you'll get a warning + buy-confirm on any future coin from those wallets. Named a dev's whole roster? Use <span className="text-[#F0C000] font-semibold">Move to Alpha</span> if they redeem themselves.</p>

      {entries.length === 0 ? (
        <div className="border border-white/10 bg-card py-12 text-center text-muted-foreground font-mono text-sm">No blocked devs yet. Hit the 💀 button on any token to flag its creator.</div>
      ) : (
        <div className="space-y-3">
          {entries.map((g) => (
            <BlockedDevCard key={g.name} group={g} onRename={rename} onSwap={(n) => swap(n, "alpha")} />
          ))}
        </div>
      )}
    </div>
  );
};

export default function Devs() {
  const [tab, setTab] = useState("alpha");
  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center bg-neon text-black"><UsersThreeIcon size={24} weight="bold" /></div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">CREATORS</h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm">Track the devs you trust and the ones you've blacklisted — by name, with all their wallets. Saved to your device.</p>
      </div>

      <div className="flex border border-white/10 w-fit">
        <button data-testid="devs-tab-alpha" onClick={() => setTab("alpha")} className={`px-5 py-2.5 text-sm font-semibold transition-colors ${tab === "alpha" ? "bg-[#C9A227]/15 text-[#F0C000]" : "text-muted-foreground hover:text-white"}`}>⭐ Alpha Devs</button>
        <button data-testid="devs-tab-leaderboard" onClick={() => setTab("leaderboard")} className={`flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold transition-colors ${tab === "leaderboard" ? "bg-[#C9A227]/15 text-[#F0C000]" : "text-muted-foreground hover:text-white"}`}>🏆 Leaderboard</button>
        <button data-testid="devs-tab-blocked" onClick={() => setTab("blocked")} className={`px-5 py-2.5 text-sm font-semibold transition-colors ${tab === "blocked" ? "bg-danger/15 text-danger" : "text-muted-foreground hover:text-white"}`}>💀 Blocked Devs</button>
        <button data-testid="devs-tab-playbook" onClick={() => setTab("playbook")} className={`flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold transition-colors ${tab === "playbook" ? "bg-neon/15 text-neon" : "text-muted-foreground hover:text-white"}`}><BookOpenIcon size={15} weight="fill" /> Playbook</button>
      </div>

      {tab === "alpha" && <AlphaDevs />}
      {tab === "leaderboard" && <DevLeaderboard />}
      {tab === "blocked" && <BlockedDevs />}
      {tab === "playbook" && <CreatorPlaybook />}
    </div>
  );
}
